/** Receipt uploads (spec 5): PDF and images, up to 6 MB (the largest real receipt is 4.6 MB). */

export const RECEIPT_MAX_BYTES = 6 * 1024 * 1024;
export const RECEIPT_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";

const CONTENT_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
};

export const RECEIPT_MESSAGES = {
  type: "Receipts must be a PDF, PNG, JPG or WebP.",
  size: "Receipts can be up to 6 MB.",
  empty: "That file is empty.",
  mismatch: "That file isn't what its name says.",
} as const;

function receiptExtension(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot < 0 ? "" : fileName.slice(dot + 1).toLowerCase();
}

/** The content type to serve inline; null for anything else (old receipts: download only). */
export function receiptContentType(fileName: string): string | null {
  return CONTENT_TYPES[receiptExtension(fileName)] ?? null;
}

export const isPdf = (fileName: string) => receiptExtension(fileName) === "pdf";

const SIGNATURES: Array<{ type: string; bytes: number[] }> = [
  { type: "application/pdf", bytes: [0x25, 0x50, 0x44, 0x46] },
  { type: "image/png", bytes: [0x89, 0x50, 0x4e, 0x47] },
  { type: "image/jpeg", bytes: [0xff, 0xd8, 0xff] },
  { type: "image/webp", bytes: [0x52, 0x49, 0x46, 0x46] },
];

/** The bytes start like the type the name claims. */
function matchesSignature(contentType: string, bytes: Uint8Array): boolean {
  const sig = SIGNATURES.find((s) => s.type === contentType);
  return sig?.bytes.every((b, i) => bytes[i] === b) ?? false;
}

/** Null when the upload is acceptable, else the message to show. */
export function receiptRefusal(fileName: string, bytes: Uint8Array): string | null {
  const type = receiptContentType(fileName);
  if (!type) return RECEIPT_MESSAGES.type;
  if (bytes.length === 0) return RECEIPT_MESSAGES.empty;
  if (bytes.length > RECEIPT_MAX_BYTES) return RECEIPT_MESSAGES.size;
  return matchesSignature(type, bytes) ? null : RECEIPT_MESSAGES.mismatch;
}
